import type { LoginTranslations } from '@/lang/interfaces/login';
import type { AppBrand } from '@/platform/app/contracts';
import type { Theme } from '@/theme/ThemeContext';

import { DefaultLoginPanel } from './DefaultLoginPanel';
import { LangDropdown } from './LangDropdown';
import { ThemeSwitch } from './ThemeSwitch';

export interface LoginSidePanelProps {
  brand: AppBrand;
  t: <K extends keyof LoginTranslations>(
    key: K,
    options?: Record<string, unknown>,
  ) => LoginTranslations[K];
  theme: Theme;
  toggleTheme: () => void;
  activeLanguage: string;
  setLanguage: (lang: string) => Promise<void>;
}

/**
 * Right half of the login page (`xl` and up). Wires the language dropdown,
 * the theme switch and the brand lockup once, then hands them to the brand's
 * `LoginPanel` or to the platform default.
 */
export const LoginSidePanel = ({
  brand,
  t,
  theme,
  toggleTheme,
  activeLanguage,
  setLanguage,
}: LoginSidePanelProps) => {
  const controls = (
    <>
      <LangDropdown
        isWhite
        defaultSelected={activeLanguage}
        items={[
          { label: 'EN', value: 'en' },
          { label: 'ES', value: 'es' },
          { label: 'PT', value: 'pt' },
        ]}
        onChange={(value) => setLanguage(value)}
      />
      <ThemeSwitch theme={theme} toggleTheme={toggleTheme} />
    </>
  );
  const lockup = (
    <div className="flex items-center text-white drop-shadow-md">
      <brand.Logo
        iconOnly={false}
        className="h-10 w-10"
        wordmarkClassName="text-2xl"
      />
    </div>
  );

  const Panel = brand.LoginPanel;
  if (Panel) return <Panel controls={controls} lockup={lockup} />;

  return (
    <DefaultLoginPanel
      controls={controls}
      lockup={lockup}
      title={t('title')}
      subtitle={t('subtitle')}
    />
  );
};
